import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectRoles, selectUser } from '../../redux/selectors/commonSelectors.ts';
import { AppRoles } from '@qcdoc/mock-runtime/contracts/enums.ts';
import type { AccessPermissionKey } from '@qcdoc/mock-runtime/contracts/accessModel.ts';
import { evaluateAccess } from '@qcdoc/mock-runtime/contracts/accessEvaluator.ts';
import { usePackagingPermission } from './usePackagingPermission.ts';

/**
 * Hook to check user permissions against roles and policies.
 * Maps legacy ASP.NET web.config `<authorization>` rules to derived booleans.
 *
 * Usage:
 * ```tsx
 * const { canManageProducts, canViewReports, isAdmin } = usePermission();
 * ```
 */
export function usePermission() {
  const roles = useSelector(selectRoles);
  const user = useSelector(selectUser);
  const profile = user?.accessProfile;
  const hasRole = useCallback((role: string) => {
    if (roles.includes(role)) return true;
    const aliases: Record<string, string[]> = {
      Admin: ['Administrator', 'SuperAdmin', 'QCDocAdmin'], CM: ['Category Manager'], GQL: ['Global Quality Lead'],
      QALab: ['QA Lab'], QALabSupervisor: ['QA Lab Supervisor'], QATech: ['Quality Documenter'],
      ReadOnly: ['QA View'], COAAdmin: ['COA Administrator'], ReportViewer: ['Report Viewer'],
      ProductOwner: ['PSD'], PackagingAdmin: ['Packaging Administrator'],
    };
    return profile?.assignments.some((assignment) => {
      const roleName = profile.roles.find((entry) => entry.roleId === assignment.roleId)?.roleName;
      return roleName === role || aliases[role]?.includes(roleName ?? '') === true;
    }) ?? false;
  }, [profile, roles]);
  const hasAnyRole = useCallback((required: string[]) => required.some(hasRole), [hasRole]);
  const hasPermission = useCallback((permission: AccessPermissionKey, pslId?: string) => {
    if (!profile) return false;
    if (pslId) return evaluateAccess(profile, permission, pslId).allowed;
    const psls = profile.psls.filter((psl) => psl.isActive);
    const recordPsls = psls.filter((psl) => !psl.isGlobal);
    return (recordPsls.length ? recordPsls.some((psl) => evaluateAccess(profile, permission, psl.pslId).allowed) : false)
      || psls.some((psl) => psl.isGlobal && evaluateAccess(profile, permission, psl.pslId).allowed);
  }, [profile]);
  const permissionOrLegacy = useCallback((permission: AccessPermissionKey, legacyRoles: string[]) =>
    profile ? hasPermission(permission) : hasAnyRole(legacyRoles), [hasAnyRole, hasPermission, profile]);

  // ── Admin super-set ──
  const adminRoles = [AppRoles.Admin, AppRoles.SuperAdmin, AppRoles.QCDocAdmin];
  const isAdmin = permissionOrLegacy('Administration.Users.Manage', adminRoles);

  // ── Products: legacy Products/web.config ──
  const canViewProducts = permissionOrLegacy('Products.View', adminRoles);
  const canManageProducts = permissionOrLegacy('Products.Edit', adminRoles);
  const canCreateProducts = permissionOrLegacy('Products.Create', adminRoles);
  const canViewStp = permissionOrLegacy('STP.View', adminRoles);
  const canManageStp = permissionOrLegacy('STP.Edit', adminRoles);
  const canCreateStp = permissionOrLegacy('STP.Create', adminRoles);
  const canApproveStp = permissionOrLegacy('STP.Approve', adminRoles);
  const canDeleteStp = permissionOrLegacy('STP.Delete', adminRoles);

  // ── VQCA: legacy VQCA/web.config ──
  // Admin, GQL, CM, PurchasingAgent, SupplyChain, QALab, QATech
  const canManageVqca = permissionOrLegacy('VQCA.View', [
    AppRoles.GQL,
    AppRoles.CM,
    AppRoles.PurchasingAgent,
    AppRoles.SupplyChain,
    AppRoles.QALab,
    AppRoles.QATech,
    ...adminRoles,
  ]);
  const canCreateVqca = permissionOrLegacy('VQCA.Create', [AppRoles.GQL, ...adminRoles]);

  // GQL Pending — Admin only in legacy
  const canAccessGqlPending = permissionOrLegacy('VQCA.SignGql', [AppRoles.GQL, ...adminRoles]);

  // CM Pending — Admin, CM, QALab
  const canAccessCmPending = permissionOrLegacy('VQCA.SignCm', [
    AppRoles.CM,
    AppRoles.QALab,
    ...adminRoles,
  ]);
  const canSignVqcaAsCm = permissionOrLegacy('VQCA.SignCm', [AppRoles.CM, ...adminRoles]);

  // ── COA: legacy COA + Experiment folders ──
  // Admin, GQL, CM, COAAdmin, QALab, QATech, ReadOnly
  const legacyCoaRoles = [
    AppRoles.COAAdmin,
    AppRoles.GQL,
    AppRoles.CM,
    AppRoles.QALab,
    AppRoles.QATech,
    AppRoles.ReadOnly,
    ...adminRoles,
  ];
  const canViewCoa = permissionOrLegacy('COA.View', legacyCoaRoles);
  const canManageCoa = canViewCoa;
  const canEditCoa = permissionOrLegacy('COA.Edit', [AppRoles.COAAdmin, AppRoles.GQL, AppRoles.CM, AppRoles.QALab, AppRoles.QATech, ...adminRoles]);

  // COA Perf Spec Results — Admin, CM only
  const canAccessCoaPerfSpec = permissionOrLegacy('COA.Edit', [
    AppRoles.CM,
    ...adminRoles,
  ]);

  // COA review (approve / conditionally-approve / fail) — supervisors only, no ReadOnly
  const canReviewCoa = permissionOrLegacy('COA.Review', [
    AppRoles.GQL,
    AppRoles.CM,
    AppRoles.QALab,
    AppRoles.COAAdmin,
    ...adminRoles,
  ]);

  // COA create (internal) — QA + COA admins, no ReadOnly
  const canCreateCoa = permissionOrLegacy('COA.Create', [
    AppRoles.COAAdmin,
    AppRoles.QALab,
    AppRoles.QATech,
    ...adminRoles,
  ]);

  // COA delete / restore — COA admins & quality leads, no ReadOnly
  const canDeleteCoa = permissionOrLegacy('COA.Delete', [
    AppRoles.COAAdmin,
    AppRoles.GQL,
    ...adminRoles,
  ]);
  const canRestoreCoa = permissionOrLegacy('COA.Restore', [AppRoles.COAAdmin, ...adminRoles]);

  // ── QA Lab: legacy Experiment/web.config ──
  // Admin, CM, QALab, QATech, ReadOnly
  const canAccessQALab = permissionOrLegacy('Samples.View', [
    AppRoles.QALab,
    AppRoles.QATech,
    AppRoles.CM,
    AppRoles.ReadOnly,
    ...adminRoles,
  ]);

  // QA Perf Spec Results — Admin, QALab, QATech, CM, ReadOnly
  const canAccessQaPerfSpec = permissionOrLegacy('Experiments.View', [
    AppRoles.QALab,
    AppRoles.QATech,
    AppRoles.CM,
    AppRoles.ReadOnly,
    ...adminRoles,
  ]);

  // ── Logistics (COQ): legacy Logistics role ──
  // Admin, Logistics, SupplyChain, ProductOwner
  const canAccessLogistics = permissionOrLegacy('COQ.View', [
    AppRoles.Logistics,
    AppRoles.SupplyChain,
    AppRoles.ProductOwner,
    ...adminRoles,
  ]);
  const canDeleteCoq = permissionOrLegacy('COQ.Delete', [AppRoles.Logistics, ...adminRoles]);

  // ── Packaging ──
  const { canReadPackaging: canManagePackaging, canWritePackaging } = usePackagingPermission();

  // ── Alerts: legacy Alert/web.config — Admin only ──
  const canManageAlerts = permissionOrLegacy('Alerts.Manage', adminRoles);

  // ── Categories: legacy CategoryLevel/web.config — Admin only ──
  const canManageCategories = permissionOrLegacy('CategoryLevels.Manage', adminRoles);

  // ── HDFX: legacy Hdfx/web.config ──
  // Admin, QATech, CM, Hdfx
  const canAccessHdfx = permissionOrLegacy('Hdfx.View', [
    AppRoles.QATech,
    AppRoles.CM,
    AppRoles.Hdfx,
    ...adminRoles,
  ]);

  // ── Reports ──
  const canViewReports = permissionOrLegacy('Reports.View', [
    AppRoles.ReportViewer,
    AppRoles.GQL,
    AppRoles.CM,
    AppRoles.QALab,
    AppRoles.QALabSupervisor,
    AppRoles.QATech,
    AppRoles.ReadOnly,
    AppRoles.SupplyChain,
    AppRoles.PurchasingAgent,
    AppRoles.Regulatory,
    AppRoles.FormulaEditor,
    AppRoles.Manager,
    AppRoles.GroupLeader,
    ...adminRoles,
  ]);
  const canViewProductReports = canViewReports;
  const canViewApprovedVendors = canViewReports;

  // ── Internal vendor management (legacy top-level Vendor module) ──
  const canViewVendors = permissionOrLegacy('Vendors.View', [
    AppRoles.GQL,
    AppRoles.CM,
    AppRoles.QALabSupervisor,
    ...adminRoles,
  ]);
  const canManageVendors = permissionOrLegacy('Vendors.Manage', [
    AppRoles.GQL,
    AppRoles.CM,
    AppRoles.QALabSupervisor,
    ...adminRoles,
  ]);

  // ── Vendor portal ──
  const canAccessVendor = hasAnyRole([
    AppRoles.Vendor,
    AppRoles.VendorAdmin,
    'SuperUser',
  ]);

  // ── Formula editing: legacy FormulaEditor role ──
  const canEditFormulas = permissionOrLegacy('Products.Edit', [
    AppRoles.FormulaEditor,
    ...adminRoles,
  ]);

  // ── Generic role checker ──
  const checkRoles = useCallback(
    (requiredRoles: string[]) => hasAnyRole(requiredRoles),
    [hasAnyRole],
  );

  return {
    // Core
    isAdmin,
    checkRoles,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAccessProfile: Boolean(profile),

    // Feature-level permissions
    canManageProducts,
    canViewProducts,
    canCreateProducts,
    canManageStp,
    canViewStp,
    canCreateStp,
    canApproveStp,
    canDeleteStp,
    canViewProductReports,
    canViewApprovedVendors,
    canManageVqca,
    canCreateVqca,
    canAccessGqlPending,
    canAccessCmPending,
    canSignVqcaAsCm,
    canManageCoa,
    canViewCoa,
    canEditCoa,
    canReviewCoa,
    canCreateCoa,
    canDeleteCoa,
    canRestoreCoa,
    canAccessCoaPerfSpec,
    canAccessQALab,
    canAccessQaPerfSpec,
    canAccessLogistics,
    canDeleteCoq,
    canManagePackaging,
    canWritePackaging,
    canManageAlerts,
    canManageCategories,
    canAccessHdfx,
    canViewReports,
    canManageVendors,
    canViewVendors,
    canAccessVendor,
    canEditFormulas,
  };
}
